<template>
    <div id="ready-contents">
        <div class="header">
            <h5>{{ $t("plans.readyContents") }}</h5>
            <div class="search-bar">
                <input type="text" placeholder="Ara..." v-model="searchQuery" @input="filterContents" />
                <i class="material-icons">search</i>
            </div>
        </div>
        <div class="ready-contents-group" v-for="group of filteredReadyContents" :key="group._id">
            <div class="ready-contents-group-head" @click="toggleActive(group._id)">
                <div class="title">{{ getContentFromLanguage(group, "name") }}</div>
                <div class="material-icons">
                    {{ isActive(group._id) ? "arrow_drop_up" : "arrow_drop_down" }}
                </div>
            </div>
            <div
                class="ready-contents-group-content"
                :class="{ active: isActive(group._id) }"
                :style="{
                    '--items': group.children.length,
                }"
            >
                <div class="ready-contents-group-content-items">
                    <div
                        class="ready-contents-group-item"
                        v-for="item of group.children"
                        :key="item._id"
                        @click="triggerReadyContent(item, 'content')"
                    >
                        <div class="ready-content-key">{{ getPressKey(item) }}</div>
                        <div
                            class="ready-content-val"
                            v-tippy="{ followCursor: true, theme: 'light', arrow: true, placement: 'bottom' }"
                            :content="getContentFromLanguage(item)"
                        >
                            {{ getContentFromLanguage(item) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { SanitizeHtml } from "../../../utils/utils";
import _ from "lodash";

export default {
    name: "ReadyContents",
    computed: {
        ...mapState({
            readyContents: (state) => state.content.contents.data,
        }),
    },
    props: {
        contact: {
            type: Object,
        },
    },
    data: () => ({
        activeTab: null,
        groupedReadyContents: [],
        filteredReadyContents: [],
        searchQuery: "",
    }),
    mounted() {
        this.initReadyContents();
    },
    methods: {
        initReadyContents() {
            let allItems = this.readyContents;
            const parents = allItems
                .filter((readyContent) => !readyContent.parentId)
                .reduce((acc, readyContent) => {
                    acc[readyContent._id] = {
                        ...readyContent,
                        children: [],
                    };
                    return acc;
                }, {});
            let readyContents = allItems.reduce((acc, readyContent) => {
                if (acc[readyContent.parentId]) {
                    readyContent.parent = allItems.find((x) => x._id === readyContent.parentId);
                    acc[readyContent.parentId].children.push(readyContent);
                }
                return acc;
            }, parents);
            this.groupedReadyContents = Object.values(readyContents);
            this.filteredReadyContents = this.groupedReadyContents;
            this.activeTab = this.groupedReadyContents[0]?._id;
        },
        getContentFromLanguage(item, key = "content", text = true) {
            const content =
                _.get(item, `languageContent.${this.$i18n.locale}.${key}`) ||
                _.get(item, `languageContent.${Object.keys(item.languageContent)[0]}.${key}`) ||
                "";
            return text ? SanitizeHtml(content, true) : content;
        },
        toggleActive(id) {
            if (this.activeTab === id) {
                this.activeTab = null;
                return;
            }
            this.activeTab = id;
        },
        isActive(id) {
            return this.activeTab === id;
        },
        getPressKey(item) {
            const keys = {
                ALT: `ALT + ${item.letter}`,
                HASH: `#${item.hash}`,
                TAB: `${item.letter} + TAB > ${item.number}`,
            };
            return keys[item.type];
        },
        triggerReadyContent(item) {
            SYSTEM_PUBSUB.publish("ready_content_used", {
                content: this.getContentFromLanguage(item, "content", false),
                contact: this.contact,
            });
        },
        filterContents() {
            if (this.searchQuery.trim() === "") {
                this.filteredReadyContents = this.groupedReadyContents;
            } else {
                this.filteredReadyContents = this.groupedReadyContents
                    .map((group) => ({
                        ...group,
                        children: group.children.filter((item) =>
                            this.getContentFromLanguage(item).toLowerCase().includes(this.searchQuery.toLowerCase())
                        ),
                    }))
                    .filter((group) => group.children.length > 0);
            }
        },
    },
    watch: {
        readyContents: {
            deep: true,
            handler() {
                this.initReadyContents();
            },
        },
    },
};
</script>
<style lang="scss" scoped>
#ready-contents {
    display: flex;
    flex-direction: column;
    color: #22272d;
    font-family: "Poppins", sans-serif;

    .header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        flex-direction: column;
        padding: 15px;
        gap: 10px;

        .search-bar {
            display: flex;
            width: 100%;
            align-items: flex-end;
            border: 1px solid #ccc;
            border-radius: 5px;
            padding: 4px 8px;
            background-color: #f1f4f7;

            input {
                border: none;
                outline: none;
                padding: 4px;
                width: 100%;
                background-color: transparent;
            }

            .material-icons {
                color: black;
            }
        }
    }

    .ready-contents-group {
        border-bottom: 1px solid #f1f4f7;
        width: 100%;

        &-head {
            display: flex;
            justify-content: space-between;
            align-items: center;
            user-select: none;
            padding: 10px;
            font-weight: bold;
            cursor: pointer;
        }

        &-content {
            width: 100%;
            height: 0px;
            transition: height 0.4s;
            overflow: hidden;

            $height: calc(((var(--items) * 20px) + (var(--items) - 1) * 4px) + 15px);

            &.active {
                height: #{$height};
                transition: all 0.4s;
            }

            &-items {
                padding: 0 10px 10px 10px;
                display: flex;
                flex-direction: column;
                gap: 4px;
            }
        }

        &-item {
            display: flex;
            gap: 4px;
            align-items: center;
            user-select: none;
            height: 20px;
            overflow: hidden;
            cursor: pointer;

            .ready-content {
                &-key {
                    background: #f1f4f7;
                    padding: 8px;
                    border-radius: 3px;
                    color: #8796af;
                    font-size: 10px;
                    font-weight: 500;
                    white-space: nowrap;
                }

                &-val {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        }
    }
}
</style>
